/* Pure helpers for the GDICarouselGridBlock — no React, no hooks. */

/* Map of "ContentJustification" dropdown values → CSS justify-content */
import {fillGDIResponsiveInheritance, hasGDIViewTypes} from '../../utils/gdi-styles'
import {parseResponsiveViewTypes} from '../../utils/gdi-responsive'

export const JUSTIFY_MAP = {
    Left: 'flex-start',
    Center: 'center',
    Right: 'flex-end',
    'Space between': 'space-between',
    'Space around': 'space-around'
}

export const toInt = (v, fallback) => {
    const n = parseInt(v, 10)
    return Number.isFinite(n) && n > 0 ? n : fallback
}

/* Build a responsive slide-width string from `tilesPerRow` and `tilesGap`.
 * Both inputs can be Chakra-style responsive objects ({base, md, lg, ...})
 * or scalars; the result mirrors whichever shape was richer.
 *
 * The gap-aware formula `(100% - (N-1) * gap) / N` matters for `flex-wrap:
 * wrap` (Grid mode): plain `100%/N` would overflow by `(N-1) * gap` and force
 * each row to a single item. For `flex-wrap: nowrap` (Carousel mode) it makes
 * the visible viewport fit exactly N tiles + (N-1) gaps. */
const BREAKPOINT_ORDER = ['base', 'sm', 'md', 'lg', 'xl', '2xl']

const slideWidthAt = (n, gap) => {
    const num = parseFloat(n)
    if (num === 0) return 'auto'
    if (!Number.isFinite(num) || num < 0) return '100%'
    const g = parseFloat(gap) || 0
    if (g === 0 || num === 1) return `calc(100% / ${num})`
    return `calc((100% - ${(num - 1) * g}px) / ${num})`
}

export const buildSlideWidth = (tilesPerRow, tilesGap) => {
    const tprObj =
        typeof tilesPerRow === 'object' && tilesPerRow !== null ? tilesPerRow : {base: tilesPerRow}
    const gapObj =
        typeof tilesGap === 'object' && tilesGap !== null ? tilesGap : {base: tilesGap || '0px'}

    let n = tprObj.base
    let g = gapObj.base
    const result = {}
    for (const bp of BREAKPOINT_ORDER) {
        if (tprObj[bp] !== undefined) n = tprObj[bp]
        if (gapObj[bp] !== undefined) g = gapObj[bp]
        if (tprObj[bp] !== undefined || gapObj[bp] !== undefined) {
            result[bp] = slideWidthAt(n, g)
        }
    }
    /* Collapse single-key result back to a scalar so consumers don't see a
     * trivially-responsive object. */
    return Object.keys(result).length === 1 && 'base' in result ? result.base : result
}

/* Width of a slide that should span `s` of the parent's `n` columns. Mirrors
 * `buildSlideWidth` but accounts for the gaps the spanned slide swallows.
 *
 * Derivation: a spanned slide takes `s` base widths plus the `s - 1` gaps
 * between them, i.e.
 *   spanned = s * baseWidth + (s - 1) * gap
 *           = s * ((100% - (n - 1) * gap) / n) + (s - 1) * gap
 *           = (s * 100% + (s - n) * gap) / n
 * which, when expressed as a CSS calc, lets the spanned tile share the
 * parent's flex track without breaking the row math. */
const spanWidthAt = (s, n, gap) => {
    const span = parseFloat(s)
    const num = parseFloat(n)
    if (!Number.isFinite(span) || span <= 1) return slideWidthAt(num, gap)
    if (!Number.isFinite(num) || num <= 0) return '100%'
    if (span >= num) return '100%'
    const g = parseFloat(gap) || 0
    if (g === 0) return `calc(${span} * 100% / ${num})`
    /* (span - n) is negative when span < n; CSS calc handles the sign. */
    return `calc((${span} * 100% + ${(span - num) * g}px) / ${num})`
}

/* Build a responsive width string for a slide that wants to span multiple
 * columns. Falls back to `buildSlideWidth(tilesPerRow, tilesGap)` semantics
 * when `colSpan` is unset, 1, or invalid at every breakpoint. */
export const buildSpanWidth = (colSpan, tilesPerRow, tilesGap) => {
    const spanObj = typeof colSpan === 'object' && colSpan !== null ? colSpan : {base: colSpan}
    const tprObj =
        typeof tilesPerRow === 'object' && tilesPerRow !== null ? tilesPerRow : {base: tilesPerRow}
    const gapObj =
        typeof tilesGap === 'object' && tilesGap !== null ? tilesGap : {base: tilesGap || '0px'}

    let s = spanObj.base
    let n = tprObj.base
    let g = gapObj.base
    const result = {}
    for (const bp of BREAKPOINT_ORDER) {
        if (spanObj[bp] !== undefined) s = spanObj[bp]
        if (tprObj[bp] !== undefined) n = tprObj[bp]
        if (gapObj[bp] !== undefined) g = gapObj[bp]
        if (spanObj[bp] !== undefined || tprObj[bp] !== undefined || gapObj[bp] !== undefined) {
            result[bp] = spanWidthAt(s, n, g)
        }
    }
    return Object.keys(result).length === 1 && 'base' in result ? result.base : result
}

/* Read the authored `ColSpan` (per-breakpoint) from any tile shape we
 * understand. Tiles in the carousel can be GDIContentTileBlock,
 * GDICarouselGridBlock, GDICtaBlock, GDIProductTileBlock... — each carries its
 * own `<TypeName>StylesPerViewType` array, but the per-view-type entry shape
 * is uniform (`{ViewTypesToApply, ColSpan, ...}`), so we scan generically.
 * Returns null when no positive ColSpan was authored at any breakpoint. */
export const getTileColSpan = (tile) => {
    if (!tile || typeof tile !== 'object') return null
    let stylesArr = null
    for (const key of Object.keys(tile)) {
        if (!key.endsWith('StylesPerViewType')) continue
        const value = tile[key]
        if (Array.isArray(value) && value.length > 0) {
            stylesArr = value
            break
        }
    }
    if (!stylesArr) return null
    const out = {}
    const applyEntry = (entry, breakpoints) => {
        const n = Number(entry?.ColSpan)
        if (!Number.isFinite(n) || n <= 0) return
        breakpoints.forEach((bp) => {
            out[bp] = n
        })
    }

    stylesArr
        .filter((entry) => !hasGDIViewTypes(entry))
        .forEach((entry) => applyEntry(entry, ['base', 'md', 'lg']))

    stylesArr
        .filter((entry) => hasGDIViewTypes(entry))
        .forEach((entry) => applyEntry(entry, parseResponsiveViewTypes(entry.ViewTypesToApply)))

    return Object.keys(out).length ? fillGDIResponsiveInheritance(out) : null
}

/* Distance (in px) between the start of one slide and the start of the next,
 * read from the live DOM so it matches the rendered layout at the current
 * breakpoint regardless of TilesPerRow / Gap values. Returns clientWidth as
 * a sensible fallback when the track has no children yet. */
export const getSlideStride = (node) => {
    const firstChild = node.firstElementChild
    if (!firstChild) return node.clientWidth
    const width = firstChild.getBoundingClientRect().width
    const cs = getComputedStyle(node)
    const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0
    return width + gap
}

/* Map the CMS `ProductCarouselGridDynamicContent` dropdown value to the
 * VickyAI hook's aiModel parameter. Returns null when no dynamic source is
 * authored (or the value isn't recognized) — caller passes 'skip' to bail
 * the hook in that case. */
export const dynamicSourceToAIModel = (source) => {
    if (typeof source !== 'string' || source.trim() === '') return null
    const s = source.toLowerCase()
    if (s.includes('bought')) return 'bought-together'
    if (s.includes('shop') && s.includes('look')) return 'shop-the-look'
    if (s.includes('semantic')) return 'semantic-search'
    if (s.includes('trending')) return 'trending'
    if (s.includes('similar')) return 'similars'
    if (s.includes('recommend')) return 'recommendations'
    return null
}

/**
 * Determines if the provided source string corresponds to the "Recently Viewed" source from Einstein.
 * @param {string} source - The source identifier to check.
 * @returns {boolean} True if the source is a recognized recently-viewed label.
 */
export const isRecentlyViewedSource = (source) => {
    if (typeof source !== 'string') return false
    const normalized = source.trim().toLowerCase()
    return normalized === 'einstein - recently viewed' || normalized === 'recently viewed'
}

/* Compute per-slide rendering metadata when the carousel is in cloned-slide
 * looping mode. Real slides carry `realIdx` (used as the key + the data-attr
 * the loop hook reads); clones carry `realIdx = null` and a region-prefixed
 * key so React doesn't reuse DOM across real and clone instances. */
export const slideMeta = (idx, tile, cloneCount, slidesLength, keyOf) => {
    if (cloneCount === 0) {
        return {realIdx: idx, key: keyOf(tile, idx)}
    }
    const isReal = idx >= cloneCount && idx < cloneCount + slidesLength
    if (isReal) {
        const realIdx = idx - cloneCount
        return {realIdx, key: keyOf(tile, realIdx)}
    }
    const region = idx < cloneCount ? 'head' : 'tail'
    return {realIdx: null, key: `${region}-${idx}-${keyOf(tile, idx)}`}
}
